import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppField from "../../design-system/components/AppField";
import AppInput from "../../design-system/components/AppInput";
import AppLink from "../../design-system/components/AppLink";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import { IconChevronLeft } from "../../design-system/icons";
import { LoginSplit } from "../login";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "../login/login.css";

// Oeffentliche Seite: neues Passwort ueber den Link aus der Passwort-vergessen-Mail
// (/reset_password?token=...).
//
// Die Mailadresse wird nicht abgefragt, sondern serverseitig aus dem Token
// aufgeloest (POST /api/web/password/validate) und nur noch angezeigt. Gesetzt
// wird das Passwort ueber POST /api/web/password/reset — auch dort ergibt sich
// das Konto aus dem Token.

const MIN_PASSWORD_LENGTH = 4;

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);

  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError("Der Link ist unvollständig. Bitte den Link aus der E-Mail verwenden.");
      setChecking(false);
      return undefined;
    }

    let cancelled = false;
    fetch(`${API_BASE_URL}/api/web/password/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (cancelled) return;
        if (!response.ok) {
          setTokenError(data?.message || "Der Link ist ungültig oder abgelaufen.");
          return;
        }
        setEmail(data?.email || "");
      })
      .catch(() => {
        if (!cancelled) {
          setTokenError("Der Link konnte nicht geprüft werden. Bitte später erneut versuchen.");
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }
    if (password !== confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/web/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        setError(data?.message || "Das Passwort konnte nicht gespeichert werden.");
        return;
      }
      setDone(true);
    } catch {
      setError("Das Passwort konnte nicht gespeichert werden. Bitte später erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return (
      <LoginSplit>
        <AppLoadingOverlay open scope="contained" />
      </LoginSplit>
    );
  }

  if (tokenError) {
    return (
      <LoginSplit>
        <div className="v2login__form">
          <div className="v2login__head">
            <h2 className="v2login__title">Link nicht gültig</h2>
            <p className="v2login__subtitle">
              Fordere einen neuen Link an, um dein Passwort zurückzusetzen.
            </p>
          </div>

          <AppCallout variant="warning" description={tokenError} />

          <div className="v2login__actions">
            <AppButton
              variant="primary"
              size="lg"
              className="v2login__submit"
              onClick={() => navigate("/forget_password")}
            >
              Neuen Link anfordern
            </AppButton>
          </div>
        </div>
      </LoginSplit>
    );
  }

  if (done) {
    return (
      <LoginSplit>
        <div className="v2login__form">
          <div className="v2login__head">
            <h2 className="v2login__title">Passwort gespeichert</h2>
            <p className="v2login__subtitle">
              Du kannst dich jetzt mit deiner E-Mail-Adresse und dem neuen Passwort anmelden.
            </p>
          </div>

          <div className="v2login__actions">
            <AppButton
              variant="primary"
              size="lg"
              className="v2login__submit"
              onClick={() => navigate("/login")}
            >
              Zum Login
            </AppButton>
          </div>
        </div>
      </LoginSplit>
    );
  }

  return (
    <LoginSplit>
      <div className="v2login__back">
        <AppLink
          onClick={() => navigate("/login")}
          leadingIcon={<IconChevronLeft sx={{ fontSize: 16 }} />}
        >
          {t("button.back", "Zurück")} zum Login
        </AppLink>
      </div>

      <form className="v2login__form v2login__fade-in" onSubmit={onSubmit}>
        <div className="v2login__head">
          <h2 className="v2login__title">Neues Passwort wählen</h2>
          <p className="v2login__subtitle">Wähle ein neues Passwort für dein TimeTab-Konto.</p>
        </div>

        <div className="v2login__fields">
          {/* Kommt aus dem Token, deshalb nur zur Bestaetigung sichtbar. */}
          <AppField label={t("registration.email", "Email")}>
            <AppInput type="email" value={email} readOnly disabled />
          </AppField>
          <AppField
            label={t("registration.password", "Passwort")}
            help={`Mindestens ${MIN_PASSWORD_LENGTH} Zeichen`}
          >
            <AppInput
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              autoFocus
            />
          </AppField>
          <AppField label={t("registration.password2", "Passwort bestätigen")}>
            <AppInput
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
            />
          </AppField>
        </div>

        {error ? (
          <AppCallout
            variant="error"
            description={error}
            style={{ marginTop: "var(--ds-space-16)" }}
          />
        ) : null}

        <div className="v2login__actions">
          <AppButton
            type="submit"
            variant="primary"
            size="lg"
            className="v2login__submit"
            disabled={saving}
          >
            {saving ? "Wird gespeichert…" : "Passwort speichern"}
          </AppButton>
        </div>
      </form>
    </LoginSplit>
  );
}
